import React from 'react'
import PropTypes from 'prop-types'
import { Box, Grid, MenuItem, TextField, Typography } from '@mui/material'
import { EMPTY_SPECIMEN, SPECIMEN_TYPES } from '../../lib/specimen'
import AnalysisSearchSelect from './AnalysisSearchSelect'

// Specimen metadata and other analyses of the patient, for the somatic wizard
// (roadmap R1.2a). Every field is optional: the purity checks compare the fitted
// purity with the pathologist's tumour percentage when it is given, and say "not
// assessed" when it is not. Links made here start unverified and are shown, never
// used as evidence, until an identity check exists.

const SelectField = ({ label, value, options, onChange, testId }) => (
  <TextField
    select
    fullWidth
    size="small"
    label={label}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    inputProps={{ 'data-testid': testId }}
  >
    <MenuItem value="">
      <em>Not recorded</em>
    </MenuItem>
    {options.map((option) => (
      <MenuItem key={option.value} value={option.value}>
        {option.label}
      </MenuItem>
    ))}
  </TextField>
)

SelectField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  testId: PropTypes.string,
}

SelectField.defaultProps = { testId: undefined }

// `errors` is accepted for callers; the type lists need no checks.
// eslint-disable-next-line no-unused-vars
const SpecimenFields = ({ specimen, onChange, errors, paired, linkedAnalyses, onLinkedAnalysesChange }) => {
  const value = { ...EMPTY_SPECIMEN, ...(specimen || {}) }
  const set = (key) => (next) => onChange({ ...value, [key]: next })

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 2 }}>
        Optional. The specimen types; anything left blank is shown as not recorded.
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <SelectField
            label="Tumour specimen type"
            value={value.tumourType}
            options={SPECIMEN_TYPES}
            onChange={set('tumourType')}
            testId="specimen-tumour-type"
          />
        </Grid>
        {paired && (
          <Grid item xs={12} sm={6}>
            <SelectField
              label="Normal specimen type"
              value={value.normalType}
              options={SPECIMEN_TYPES}
              onChange={set('normalType')}
              testId="specimen-normal-type"
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <AnalysisSearchSelect
            multiple
            value={linkedAnalyses}
            onChange={onLinkedAnalysesChange}
            label="Other analyses of this patient"
            helperText={
              paired
                ? 'Optional. Analyses of the matched normal upload are linked automatically. Links start unverified.'
                : 'Optional: the matched normal or a germline analysis, or an RNA analysis, of the same patient. Links start unverified.'
            }
          />
        </Grid>
      </Grid>
    </Box>
  )
}

SpecimenFields.propTypes = {
  specimen: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.object,
  paired: PropTypes.bool,
  linkedAnalyses: PropTypes.array,
  onLinkedAnalysesChange: PropTypes.func.isRequired,
}

SpecimenFields.defaultProps = {
  specimen: EMPTY_SPECIMEN,
  errors: {},
  paired: false,
  linkedAnalyses: [],
}

export default SpecimenFields
